import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { AddToCartModalData, CartFacade } from '@boels/shared-state';
import {
  Cart,
  CmsComponent,
  CmsComponentData,
  OrderEntry,
  OrderEntryGroup,
  UspComponentModel,
  UspDirection,
} from '@boels/models';
import {
  asyncScheduler,
  filter,
  Observable,
  observeOn,
  Observer,
  pipe,
  take,
  tap,
} from 'rxjs';
import { UspComponent } from '../../usp/usp.component';
import {
  CartEntryEvent,
  EcommerceTrackingService,
  ModalService,
} from '@boels/helpers';
import { OrderEntryComponent, WellComponent } from '@boels/elements';
import { AddToCartModalComponent } from '../../add-to-cart-modal/add-to-cart-modal.component';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'boels-shopping-cart',
  imports: [
    CommonModule,
    DatePickerComponent,
    TranslocoModule,
    RouterModule,
    FontAwesomeModule,
    DatePipe,
    UspComponent,
    WellComponent,
    OrderEntryComponent,
    ButtonComponent,
  ],
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['addToCart'],
    },
  ],
})
export class ShoppingCartComponent {
  #modalService = inject(ModalService);
  #cartFacade = inject(CartFacade);
  #ecommerceTrackingService = inject(EcommerceTrackingService);

  @Input()
  componentData: {
    data$: Observable<CmsComponent>;
  };

  cartUspNodes$: CmsComponentData<UspComponentModel> = {
    data$: new Observable((observer: Observer<UspComponentModel>) => {
      const uspComponentModel: UspComponentModel = {
        direction: UspDirection.VERTICAL,
        isCartUsp: true,
      };
      observer.next(uspComponentModel);
      observer.complete();
    }),
  };

  datesSelected: boolean;
  datesPassed: boolean;
  today = new Date();
  cutOffTimePassed: boolean;
  faExclamationCircle = faExclamationCircle;

  get cart$(): Observable<Cart> {
    return this.#cartFacade
      .getActiveCart()
      .pipe(filter(Boolean), this.#setMandatoryAccessoriesFlag());
  }

  get cartLoading$(): Observable<boolean> {
    return this.#cartFacade.getCartLoading();
  }

  onDatesSelected(
    $event: { startDate: string | Date; endDate?: string | Date },
    cutOffTimestamp: string | undefined,
  ): void {
    this.setDatesSelected($event);
    this.cutOffTimePassed = this.isCutOffTimePassed(
      $event?.startDate.toString(),
      cutOffTimestamp,
    );
  }

  onDatesPassed($event: boolean): void {
    this.datesPassed = $event;
  }

  setDatesSelected($event: {
    startDate: string | Date;
    endDate?: string | Date;
  }): void {
    this.datesSelected = $event ? true : false;
  }

  isCutOffTimePassed(
    startDate: string,
    cutOffTimestamp?: string | undefined,
  ): boolean {
    const currentTime = new Date().getTime();
    const cuttOffTime = cutOffTimestamp && new Date(cutOffTimestamp).getTime();
    if (cuttOffTime)
      return cuttOffTime <= currentTime && this.isToday(startDate);
    return false;
  }

  isToday(startDate: string): boolean {
    const start = new Date(startDate);
    return start.toDateString() === this.today.toDateString();
  }

  deleteOrderEntry(orderEntry: OrderEntryGroup, entryNumber: number) {
    const { accessories } = orderEntry;
    const orderEntries = [orderEntry as OrderEntry, ...(accessories || [])];
    this.cart$
      .pipe(
        tap((cart) => {
          this.#ecommerceTrackingService.setCartEntryEvent(
            cart.guid,
            orderEntries,
            CartEntryEvent.RemoveFromCart,
          );
        }),
        take(1),
        observeOn(asyncScheduler),
      )
      .subscribe();
    this.#cartFacade.removeEntry(entryNumber);
  }

  openAddToCartModal(orderEntryGroup: OrderEntryGroup, orderEntry: OrderEntry) {
    this.#modalService.open(AddToCartModalComponent, {
      panelClass: 'modal-accessories-view',
      data: {
        entry: orderEntryGroup,
        productToEdit: orderEntry.product,
      } as AddToCartModalData,
    });
  }

  #setMandatoryAccessoriesFlag() {
    return pipe(
      tap((cart: Cart) => {
        cart.entries?.forEach((entry) => {
          entry?.accessories?.forEach((accessory) => {
            accessory.isMandatory =
              accessory.accessoryType?.includes('MANDATORY');
          });
        });
      }),
    );
  }

  protected readonly faArrowRight = faArrowRight;
}
